import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/components/auth/AuthContext';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  toggle: () => void;
}

// Module-level variables for animation and API debouncing (Issue 1.2 & 1.3)
let currentAnimationFrame: number | null = null;
let persistTimeout: ReturnType<typeof setTimeout> | null = null;

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getSystemPref(): ThemeMode {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function getInitialTheme(): ThemeMode {
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return getSystemPref();
}

function applyThemeAttr(theme: ThemeMode) {
  const el = document.documentElement;
  el.setAttribute('data-theme', theme === 'dark' ? 'dark' : 'light');
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>('light');
  const { user } = useAuth();
  const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');

  useEffect(() => {
    const t = getInitialTheme();
    setThemeState(t);
    applyThemeAttr(t);
  }, []);

  // Sync theme from authenticated user preference (without persisting back)
  useEffect(() => {
    if (user && typeof user.dark_theme === 'boolean') {
      const desired: ThemeMode = user.dark_theme ? 'dark' : 'light';
      if (desired !== theme) {
        // Apply without calling backend again
        try { document.documentElement.setAttribute('data-theme-animating', ''); } catch { }
        setThemeState(desired);
        animateGradientStops(desired);
        try { localStorage.setItem('theme', desired); } catch { }
        window.setTimeout(() => {
          try { document.documentElement.removeAttribute('data-theme-animating'); } catch { }
        }, 750); // Issue 1.1: Reduced from 1200ms to 750ms
      }
    }
    // We intentionally ignore animateGradientStops reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.dark_theme]);

  function parseRGB(val: string): [number, number, number] | null {
    const m = val.trim().match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (!m) return null;
    return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
  }

  function toRGBString([r, g, b]: [number, number, number]) {
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
  }

  function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

  function lerpRGB(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
    return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
  }

  function animateGradientStops(toTheme: ThemeMode) {
    try {
      // Issue 1.2: Cancel any ongoing animation to prevent concurrent loops
      if (currentAnimationFrame !== null) {
        cancelAnimationFrame(currentAnimationFrame);
        currentAnimationFrame = null;
      }

      const root = document.documentElement;
      const bgEl = document.querySelector<HTMLElement>('.global-gradient-bg');
      if (!bgEl) return;
      const elRef = bgEl; // capture non-null reference for closure

      // Capture current token values BEFORE theme switch
      const csBefore = getComputedStyle(root);
      const cur1Str = csBefore.getPropertyValue('--color-bg1').trim();
      const cur2Str = csBefore.getPropertyValue('--color-bg2').trim();
      const cur1 = parseRGB(cur1Str);
      const cur2 = parseRGB(cur2Str);
      if (!cur1 || !cur2) return;

      // Apply theme to get target tokens
      applyThemeAttr(toTheme);
      const csAfter = getComputedStyle(root);
      const tar1Str = csAfter.getPropertyValue('--color-bg1').trim();
      const tar2Str = csAfter.getPropertyValue('--color-bg2').trim();
      const goal1 = parseRGB(tar1Str);
      const goal2 = parseRGB(tar2Str);
      if (!goal1 || !goal2) return;

      // Capture starting and target colors for the animation closure
      const start1: [number, number, number] = cur1;
      const start2: [number, number, number] = cur2;
      const target1: [number, number, number] = goal1;
      const target2: [number, number, number] = goal2;

      // Issue 1.1: Reduced duration from 1200ms to 700ms for snappier transitions
      const duration = 700;

      const start = performance.now();
      function step(now: number) {
        const t = Math.min(1, (now - start) / duration);
        const c1 = toRGBString(lerpRGB(start1, target1, t));
        const c2 = toRGBString(lerpRGB(start2, target2, t));
        elRef.style.setProperty('--color-bg1', c1);
        elRef.style.setProperty('--color-bg2', c2);
        if (t < 1) {
          // Issue 1.2: Store RAF ID for potential cancellation
          currentAnimationFrame = requestAnimationFrame(step);
        } else {
          currentAnimationFrame = null;
          // Clear overrides to inherit token values
          elRef.style.removeProperty('--color-bg1');
          elRef.style.removeProperty('--color-bg2');
        }
      }
      currentAnimationFrame = requestAnimationFrame(step);
    } catch { /* no-op */ }
  }

  const setTheme = (t: ThemeMode) => {
    const el = document.documentElement;
    // Gate color transitions to theme-switch moments only
    try { el.setAttribute('data-theme-animating', ''); } catch { }
    setThemeState(t);
    // Animate gradient color stops to avoid any snap
    animateGradientStops(t);
    try { localStorage.setItem('theme', t); } catch { }
    // Issue 1.1: Clear the animating flag after reduced transition window (750ms instead of 1200ms)
    window.setTimeout(() => {
      try { el.removeAttribute('data-theme-animating'); } catch { }
    }, 750);
    // Issue 1.3: Debounced persistence to backend to prevent rapid toggle spam
    if (persistTimeout) clearTimeout(persistTimeout);
    persistTimeout = setTimeout(async () => {
      try {
        await fetch(`${API_BASE}/api/profiles/me/theme`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ dark_theme: t === 'dark' }),
        });
      } catch { /* no-op */ }
    }, 500);
  };

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark')
  }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
